import isEmptyLodash from 'lodash/isEmpty'
import isObjectLodash from 'lodash/isObject'
import cloneDeepLodash from 'lodash/cloneDeep'
import cloneDeepWithLodash from 'lodash/cloneDeepWith'
import isArrayLodash from 'lodash/isArray'
import isEqualLodash from 'lodash/isEqual'
import forEachLodash from 'lodash/forEach'
import upperFirstLodash from 'lodash/upperFirst'
import debounceLodash from 'lodash/debounce'

import isBooleanLodash from 'lodash/isBoolean'
import isFunctionLodash from 'lodash/isFunction'
import isNumberLodash from 'lodash/isNumber'
import isIntegerLodash from 'lodash/isInteger'
import isStringLodash from 'lodash/isString'

export function isEmpty (val) {
  return isEmptyLodash(val)
}

export function isObject (val) {
  return isObjectLodash(val)
}

export function cloneDeep (val) {
  return cloneDeepLodash(val)
}

/**
 * Deeply merges two values.
 *
 * Arrays are replaced, not merged by index.
 * `null` and `undefined` values from the override input are ignored.
 *
 * @param {*} baseValue
 * @param {*} overrideValue
 * @returns {*}
 */
/**
 * Whether a value is a component definition rather than plain data.
 *
 * They must never be cloned: a clone is a different component as far as Vue is
 * concerned, so a config rebuilt with one tears the mounted component down and mounts
 * a fresh copy in its place - a map inside a config would refetch and redraw itself
 * every time anything else in that config changed.
 */
function isComponentDefinition (value) {
  if (!isObjectLodash(value) || isArrayLodash(value)) {
    return false
  }

  return typeof value.render === 'function' ||
    typeof value.setup === 'function' ||
    typeof value.__file === 'string' ||
    value._Ctor !== undefined ||
    value._isVue === true
}

function cloneConfig (value) {
  return cloneDeepWithLodash(value, (current) => isComponentDefinition(current) ? current : undefined)
}

export function mergeDeep (baseValue, overrideValue) {
  const baseConfig = cloneConfig(baseValue)
  const overrideConfig = cloneConfig(overrideValue)

  if (!isObject(baseConfig) || !isObject(overrideConfig) || isArray(baseConfig) || isArray(overrideConfig)) {
    return overrideConfig === undefined ? baseConfig : overrideConfig
  }

  objectKeys(overrideConfig).forEach((key) => {
    const currentValue = overrideConfig[key]

    if (!isSet(currentValue)) {
      return
    }

    if (isComponentDefinition(currentValue)) {
      baseConfig[key] = currentValue
      return
    }

    if (isObject(currentValue) && !isArray(currentValue) && isObject(baseConfig[key]) && !isArray(baseConfig[key])) {
      baseConfig[key] = mergeDeep(baseConfig[key], currentValue)
      return
    }

    baseConfig[key] = currentValue
  })

  return baseConfig
}

export function isArray (val) {
  return isArrayLodash(val)
}

export function isEqual (...val) {
  return isEqualLodash(...val)
}

export function isBoolean (val) {
  return isBooleanLodash(val)
}

export function isFunction (val) {
  return isFunctionLodash(val)
}

export function isNumber (val) {
  return isNumberLodash(val)
}

export function isInteger (val) {
  return isIntegerLodash(val)
}

export function numberToString (val) {
  return String(val)
}

export function isNumeric (val) {
  return /^-{0,1}\d{1,12}(\.\d{1,10})?$/.test(numberToString(val).trim())
}

export function isString (val) {
  return isStringLodash(val)
}

export function upperFirst (val) {
  return upperFirstLodash(val)
}

export function debounce (func, wait, options) {
  return debounceLodash(func, wait, options)
}

/**
 * Checks whether a value is defined.
 * A value is considered "set" if it is neither `null` nor `undefined`.
 *
 * @param {*} val - The value to check.
 * @returns {boolean} True if the value is not null or undefined, otherwise false.
 */
export function isSet (val) {
  return val !== null && val !== undefined
}

/**
 * Get a boolean value from the give value.
 *
 * @param {*} val - The value to check.
 * @returns {boolean} True if the value is not null or undefined, otherwise false.
 */
export function getBooleanFromValue (val) {
  return !!val
}

/**
 * Deeply flattens any object or array into a single-level object.
 *
 * @param {Object|Array} input - The object to flatten
 * @param {Object} options
 * @param {string} [options.separator='.'] - Key separator
 * @param {boolean} [options.includeArrays=true] - Whether to flatten arrays
 * @param {number} [options.maxDepth=Infinity] - Max recursion depth
 * @returns {Object}
 */
export function flattenDeep (input, options = {}) {
  const {
    separator = '.',
    includeArrays = true,
    maxDepth = Infinity
  } = options

  const result = {}

  function recurse (value, currentKey = '', depth = 0) {
    // Stop if max depth reached
    if (depth > maxDepth) {
      result[currentKey] = value
      return
    }

    // Handle null or primitive
    if (value === null || typeof value !== 'object') {
      result[currentKey] = value
      return
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (!includeArrays) {
        result[currentKey] = value
        return
      }

      if (value.length === 0) {
        result[currentKey] = []
        return
      }

      value.forEach((item, index) => {
        const newKey = currentKey
          ? `${currentKey}${separator}${index}`
          : `${index}`

        recurse(item, newKey, depth + 1)
      })

      return
    }

    // Handle objects
    const keys = Object.keys(value)

    if (keys.length === 0) {
      result[currentKey] = {}
      return
    }

    keys.forEach((key) => {
      const newKey = currentKey
        ? `${currentKey}${separator}${key}`
        : key

      recurse(value[key], newKey, depth + 1)
    })
  }

  recurse(input)

  return result
}

/**
 * Deeply unwraps nested objects by removing wrapper keys
 *
 * @param {Object} obj
 * @param {Object} options
 * @param {string[]} [options.keys] - keys to unwrap (if empty → unwrap all)
 * @param {boolean} [options.deep=true] - unwrap recursively
 * @returns {Object}
 */
export function unwrap (obj, options = {}) {
  const {
    keys = [],
    deep = false
  } = options

  function shouldUnwrap (key) {
    return keys.length === 0 || keys.includes(key)
  }

  function recurse (current) {
    if (!isObject(current)) return current

    let result = {}

    Object.entries(current).forEach(([key, value]) => {
      // If key should be unwrapped
      if (isObject(value) && shouldUnwrap(key)) {
        const unwrapped = deep ? recurse(value) : value

        result = {
          ...result,
          ...unwrapped
        }
      }
      else {
        result[key] = deep ? recurse(value) : value
      }
    })

    return result
  }

  return recurse(obj)
}

/**
 * Check if a value type of `File` instance.
 *
 * @param {*} val - The value to check.
 * @returns {boolean} True if the value type of `File` instance, otherwise false.
 */
export function isFile (val) {
  return val instanceof File
}

export const forEach = forEachLodash

export const isNaN = Number.isNaN

/**
 * Determines whether a value should be considered "filled" (i.e. meaningfully populated).
 *
 * This utility goes beyond simple truthy/falsy checks by applying rules for common
 * "empty-like" values such as `0`, `"0"`, `"false"`, `"null"`, `"undefined"`, empty strings,
 * empty arrays, and empty objects.
 *
 * Behavior can be customized via options, including overriding specific values,
 * handling whitespace, and providing a custom validator.
 *
 * @param {*} val - The value to evaluate.
 *
 * @param {Object} [options={}] - Configuration options.
 *
 * @param {Array<*>} [options.allowedValues=[]]
 * Values that should always be considered filled, regardless of default logic.
 *
 * @param {Array<*>} [options.disallowedValues=[]]
 * Values that should always be considered NOT filled, regardless of default logic.
 *
 * @param {boolean} [options.trimStrings=true]
 * Whether to trim string values before evaluation.
 *
 * @param {boolean} [options.treatWhitespaceAsEmpty=true]
 * Whether strings containing only whitespace should be treated as empty.
 *
 * @param {boolean} [options.treatNaNAsEmpty=true]
 * Whether `NaN` should be treated as an empty value.
 *
 * @param {boolean} [options.checkEmptyArrays=true]
 * Whether empty arrays (`[]`) should be treated as empty.
 *
 * @param {boolean} [options.checkEmptyObjects=true]
 * Whether empty objects (`{}`) should be treated as empty.
 *
 * @param {(val: *) => (boolean|undefined)} [options.customValidator=null]
 * Optional custom validation function. If it returns a boolean, that result is used.
 * If it returns `undefined`, the default logic continues.
 *
 * @returns {boolean}
 * Returns `true` if the value is considered filled, otherwise `false`.
 *
 * @example
 * isFilled(true) // true
 * isFilled(false) // false
 * isFilled('hello') // true
 * isFilled('') // false
 * isFilled(' ') // false
 * isFilled(0) // false
 * isFilled('0') // false
 *
 * @example
 * // Override behavior
 * isFilled('0', { allowedValues: ['0'] }) // true
 *
 * @example
 * // Custom validator
 * isFilled('N/A', {
 *   customValidator: (val) => val !== 'N/A'
 * }) // false
 *
 * @example
 * // Allow empty arrays
 * isFilled([], { checkEmptyArrays: false }) // true
 */
export function isFilled (val, options = {}) {
  const {
    allowedValues = [],
    disallowedValues = [],
    trimStrings = true,
    treatWhitespaceAsEmpty = true,
    treatNaNAsEmpty = true,
    checkEmptyObjects = true,
    checkEmptyArrays = true,
    customValidator = null
  } = options

  // 1. Explicit overrides (the highest priority)
  if (allowedValues.some(v => isEqual(v, val))) return true
  if (disallowedValues.some(v => isEqual(v, val))) return false

  // 2. Custom validator (user-defined logic)
  if (customValidator && isFunction(customValidator)) {
    const result = customValidator(val)
    if (isBoolean(result)) return result
  }

  // 3. Null / undefined
  if (!isSet(val)) return false

  // 4. Boolean
  if (isBoolean(val)) return val

  // 5. Number
  if (isNumber(val)) {
    if (treatNaNAsEmpty && isNaN(val)) return false
    return val !== 0
  }

  // 6. String
  if (isString(val)) {
    let str = val

    if (trimStrings) str = str.trim()

    if (str === '') {
      return !treatWhitespaceAsEmpty
    }

    const normalized = str.toLowerCase()

    if (
      normalized === 'false' ||
      normalized === '0' ||
      normalized === 'null' ||
      normalized === 'undefined'
    ) {
      return false
    }

    return true
  }

  // 7. Array
  if (isArray(val)) {
    if (!checkEmptyArrays) return true
    return val.length > 0
  }

  // 8. Object
  if (isObject(val)) {
    if (!checkEmptyObjects) return true
    return Object.keys(val).length > 0
  }

  // 9. Fallback
  return !!val
}

/**
 * Check if an object has a property.
 *
 * @param {Object} object - Object to check.
 *
 * @param {string} property - Property to check.
 *
 * @returns {boolean}
 * Returns `true` if the object has the specified property, otherwise `false`.
 */
export function hasProperty (object, property) {
  if (!isObject(object)) {
    return false
  }

  const propType = typeof property
  if (propType !== 'string' && propType !== 'symbol') {
    return false
  }

  const hasOwn = Object.hasOwn
  if (typeof hasOwn === 'function') {
    try {
      return hasOwn(object, property)
    }
    catch (e) {
      // fallback below
      console.error(e)
    }
  }

  return Object.prototype.hasOwnProperty.call(object, property)
}

/**
 * Check if an object has a direct own property with the given key.
 *
 * @param {Object} object - Object to check.
 * @param {string} key - Key to check.
 *
 * @returns {boolean}
 */
export function has (object, key) {
  return isObject(object) && Object.prototype.hasOwnProperty.call(object, key)
}

/**
 * Extract object properties (keys) into an array.
 *
 * @param {Object} object - Object to extract keys from.
 *
 * @returns {string[]}
 * Returns an array of the object's own property names.
 * If the provided value is not an object, it returns `[]`.
 */
export function objectKeys (object) {
  if (!isObject(object)) {
    return []
  }

  return Object.keys(object)
}

/**
 * Extract object values into an array.
 *
 * @param {Object} object - Object to extract values from.
 *
 * @returns {*[]}
 * Returns an array of the object's values.
 * If the provided value is not an object, it returns `[]`.
 */
export function objectValues (object) {
  if (!isObject(object)) {
    return []
  }

  return Object.values(object)
}

/**
 * Freezes an object to prevent modifications.
 *
 * @template {Object} T
 * @param {T} object - Object to freeze.
 *
 * @returns {Readonly<T>|undefined}
 * Returns the frozen object or `undefined` if the provided value is not an object.
 */
export function freezeObject (object) {
  if (!isObject(object)) {
    return
  }

  return Object.freeze(object)
}

/**
 * Parse value to integer.
 *
 * @param val
 * @param radix
 * @return {number}
 */
export function parseInt (val, radix = 10) {
  return Number.parseInt(val, radix)
}
